/**
 * TerminalManagement.jsx — 회사별 단말기 ID 관리
 *
 * 단말기 ID 관리 권한을 가진 사용자가 해당 회사 마케팅 담당자의
 * 단말기 ID를 등록·수정하는 범용 페이지.
 *
 * props:
 *   companyCode: string  — 회사 코드 (예: 'DOT', 'LNJ')
 *   companyName: string  — 회사명 (표시용)
 *
 * API: GET/PUT /api/admin/ext/:code/terminal-users
 * 저장 시 해당 회사 카드승인 데이터에 자동 연동됩니다.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '../../api/apiClient';
import { useToast } from '../../components/Toast';

// ── 스타일 상수 ────────────────────────────────────────────────────────────────
const PAGE_BG = {
  minHeight: '100vh',
  background: 'var(--bg-subtle)',
  padding: '24px 20px',
  boxSizing: 'border-box',
};
const HEADER = { marginBottom: 20 };
const TITLE = { fontSize: 20, fontWeight: 700, color: 'var(--text-sub)', margin: 0 };
const SUB   = { fontSize: 13, color: 'var(--text-muted)', marginTop: 4 };
const CARD  = {
  background: 'var(--bg-card)',
  borderRadius: 12,
  border: '1px solid var(--border)',
  overflow: 'hidden',
};
const TABLE = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const TH = {
  background: 'var(--bg-subtle)',
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: 600,
  color: 'var(--text-sub)',
  borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};
const TD = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--bg-subtle)',
  color: 'var(--text-sub)',
  verticalAlign: 'middle',
};
const INPUT = {
  width: '100%',
  padding: '6px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  color: 'var(--text-sub)',
  background: 'var(--bg-card)',
  boxSizing: 'border-box',
  outline: 'none',
};
const BTN_SAVE = {
  padding: '6px 14px',
  background: '#6366f1',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const BTN_SAVE_DISABLED = { ...BTN_SAVE, background: 'var(--text-muted)', cursor: 'not-allowed' };

export default function TerminalManagement({ companyCode: propCode, companyName: propName }) {
  const { showToast } = useToast();
  // props 우선, 없으면 URL :code 파라미터 사용 (AdminLayout 라우트에서 마운트 시)
  const params = useParams();
  const code  = ((propCode || params.code) || 'DOT').toUpperCase();

  // 마케터 목록
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  // API에서 가져온 회사명 (propName이 없을 때 사용)
  const [fetchedName, setFetchedName] = useState('');

  // 인라인 편집 상태: { [userId]: string }
  const [drafts, setDrafts] = useState({});
  // 저장 중인 userId Set
  const [saving, setSaving] = useState(new Set());

  // ── 데이터 로드 ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiClient.get(`/api/admin/ext/${code}/terminal-users`);
    setLoading(false);
    if (!r.ok) { showToast('불러오기 실패', 'error'); return; }
    // API 응답의 company_name 저장 (prop으로 받지 못한 경우 표시용)
    if (r.data?.company_name) setFetchedName(r.data.company_name);
    const list = r.data?.users || [];
    setUsers(list);
    // draft 초기화: 현재 DB 값으로 설정
    const d = {};
    for (const u of list) {
      d[u.id] = u.company_terminal_id || '';
    }
    setDrafts(d);
  }, [code, showToast]);

  useEffect(() => { load(); }, [load]);

  // prop으로 받은 회사명 우선, 없으면 API 응답에서 가져온 회사명, 그것도 없으면 코드 그대로 표시
  const label = propName || fetchedName || code;

  // ── 저장 ────────────────────────────────────────────────────────────────────
  async function save(userId) {
    const draft = drafts[userId];
    if (draft === undefined) return;

    // 변경 여부 확인
    const orig = users.find((u) => u.id === userId);
    const tid  = draft.trim() || null;
    if (tid === (orig?.company_terminal_id || null)) {
      showToast('변경 사항이 없습니다.', 'error');
      return;
    }

    setSaving((prev) => new Set([...prev, userId]));
    const r = await apiClient.put(`/api/admin/ext/${code}/terminal-users/${userId}`, {
      terminal_id: tid,
    });
    setSaving((prev) => { const s = new Set(prev); s.delete(userId); return s; });

    if (!r.ok) { showToast(r.data?.message || '저장 실패', 'error'); return; }

    // 성공 시 목록 갱신
    setUsers((prev) => prev.map((u) =>
      u.id === userId ? { ...u, company_terminal_id: tid } : u
    ));
    showToast(`${orig?.name || ''} 단말기 ID가 저장되었습니다.`, 'success');
  }

  // ── 변경 여부 체크 ──────────────────────────────────────────────────────────
  function isDirty(userId) {
    const draft = drafts[userId];
    const orig  = users.find((u) => u.id === userId);
    if (draft === undefined || !orig) return false;
    return (draft.trim() || null) !== (orig.company_terminal_id || null);
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={PAGE_BG}>
      {/* 헤더 */}
      <div style={HEADER}>
        <h2 style={TITLE}>단말기 ID 관리 ({label})</h2>
        <p style={SUB}>
          마케팅 담당자의 {label} 단말기 ID를 등록·수정합니다.
          저장 시 매출 관리 카드승인 데이터에 자동 연동됩니다.
        </p>
      </div>

      <div style={CARD}>
        <table style={TABLE}>
          <thead>
            <tr>
              <th style={TH}>성명</th>
              <th style={TH}>{label} 단말기ID</th>
              <th style={{ ...TH, width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={3} style={{ ...TD, textAlign: 'center', color: 'var(--text-muted)' }}>
                  불러오는 중...
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={3} style={{ ...TD, textAlign: 'center', color: 'var(--text-muted)' }}>
                  마케팅 담당자가 없습니다.
                </td>
              </tr>
            )}
            {!loading && users.map((u) => {
              const draft    = drafts[u.id] ?? '';
              const dirty    = isDirty(u.id);
              const isSaving = saving.has(u.id);

              return (
                <tr key={u.id} style={{ background: dirty ? '#fefce8' : undefined }}>
                  {/* 성명 */}
                  <td style={TD}>
                    <span style={{ fontWeight: 600 }}>{u.name}</span>
                  </td>

                  {/* 단말기 ID 입력 */}
                  <td style={TD}>
                    <input
                      style={INPUT}
                      placeholder={`${label} 단말기 ID`}
                      value={draft}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [u.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') save(u.id); }}
                    />
                  </td>

                  {/* 저장 버튼 */}
                  <td style={{ ...TD, textAlign: 'center' }}>
                    <button
                      style={dirty && !isSaving ? BTN_SAVE : BTN_SAVE_DISABLED}
                      disabled={!dirty || isSaving}
                      onClick={() => save(u.id)}
                    >
                      {isSaving ? '저장 중...' : '저장'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
